import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnInit,
  inject,
  EventEmitter,
  Output,
  booleanAttribute,
  Inject,
  signal
} from '@angular/core';
import { Params, Router, RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import { ButtonModule } from 'primeng/button';

import { Environment, ENVIRONMENT } from '@environment';
import { UiMovieCardSaveButtonComponent } from '@usheru-hff/community-elements/ui-movie-card-save-button';
import { UiMovieCardWatchedButtonComponent } from '@usheru-hff/community-elements/ui-movie-card-watched-button';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { MovieService } from '@usheru-hff/sdk/movie';
import { TrackingService } from '@usheru-hff/sdk/tracking';
import { getMovieGenres, getMovieTags, Movie, PageType, UserRole } from '@usheru-hff/shared/domain';
import { VideoFullscreenService } from '@usheru-hff/shared/ui-video';
import { getDateFromDDMMYYYY } from '@usheru-hff/shared/utils';
import { CookiesSettingsType, SharedUtilsCookiesModule } from '@usheru-hff/shared/utils-cookies';
import { SharedUtilsImageModule } from '@usheru-hff/shared/utils-image';
import { ShowtimeStreamingService } from '@usheru-hff/shared/utils-movie';
import { SharedUtilsTranslationModule, TranslationsService } from '@usheru-hff/shared/utils-translation';

@Component({
  selector: 'ush-movie-card',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    UiMovieCardSaveButtonComponent,
    UiMovieCardWatchedButtonComponent,
    SharedUtilsCookiesModule,
    SharedUtilsTranslationModule,
    SharedUtilsImageModule,
    ButtonModule,
    FontAwesomeModule
  ],
  templateUrl: './feature-movie-card.component.html',
  styleUrl: './feature-movie-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeatureMovieCardComponent implements OnInit {
  @Input() movie: Movie;
  @Input({ transform: booleanAttribute }) showReleaseMessage: boolean = false;
  @Input() orientation: 'landscape' | 'portrait' = 'landscape';
  @Input() type: PageType = 'in-cinemas';
  @Input() loading: boolean = false;
  @Input() sizes: string;
  @Input() navigation: boolean = true;
  @Input() showReleaseDate: boolean = false;

  @Output() movieClick = new EventEmitter<Movie>();

  @HostBinding('class.placeholder') get isPlaceholder() {
    return this.loading;
  }

  titlePosition: 'inside' | 'outside' = 'inside';
  releaseMessage: string = '';
  movieImg: string = '';
  redirectUrl: string = '';
  redirectQueryParams: Params = {};
  enableCommunityFeature = false;

  width: number;
  height: number;

  private translationsService = inject(TranslationsService);
  private movieService = inject(MovieService);
  private channelService = inject(ChannelService);
  private videoService = inject(VideoFullscreenService);
  private showtimeStreamingService = inject(ShowtimeStreamingService);
  private trackingService = inject(TrackingService);
  private router = inject(Router);
  faPlay = faPlay;
  showTrailerButton = false;
  formattedReleaseDate = signal<string | undefined>(undefined);
  /**
   * Interacts with the {@link CookiesCheckDirective}.
   */
  cookiesRequired: CookiesSettingsType[] = ['functionality_storage'];

  constructor(@Inject(ENVIRONMENT) private env: Environment) {
    this.showTrailerButton = !!this.env?.customPageParams?.showTrailerButton;
  }

  ngOnInit(): void {
    this.setUpImgSizes();
    this.setUpReleaseMessage();
    this.setUpMovieImg();
    this.setUpRedirectUrl();
    this.setUpQueryParams();
    this.setUpTitlePosition();
    this.setUpCommunityElementsIfExist();
    this.setUpReleaseDate();
  }

  private setUpImgSizes() {
    if (this.orientation === 'landscape') {
      this.width = 400;
      this.height = 225;
    } else {
      this.width = 225;
      this.height = 400;
    }
    const defaultLandscapeSizes =
      '(max-width:576px) 90vw, (max-width:776px) 45vw, (max-width: 991px) 30vw, (max-width: 1500px) 20vw, 16vw';
    const defaultPortraitSizes =
      '(max-width:576px) 50vw, (max-width:776px) 50vw, (max-width: 991px) 30vw, (max-width: 1500px) 20vw, 16vw';
    this.sizes = this.sizes || (this.orientation === 'landscape' ? defaultLandscapeSizes : defaultPortraitSizes);
  }

  private setUpCommunityElementsIfExist() {
    if (this.channelService.isRolePresent(UserRole.community)) {
      // do something
      this.enableCommunityFeature = true;
    }
  }

  private setUpReleaseDate() {
    const stringDate = this.movie.locReleaseDate || this.movie.releaseDate;
    if (stringDate) {
      const date = getDateFromDDMMYYYY(stringDate);
      this.formattedReleaseDate.set(this.movieService.getFormattedDate(date));
    }
  }

  private setUpRedirectUrl() {
    this.redirectUrl = `/movie/${this.movie.friendlyUrl}`;
  }

  private setUpQueryParams() {
    this.redirectQueryParams = {
      whereToWatch: this.type
    };
  }

  private setUpTitlePosition() {
    if (this.orientation === 'portrait') {
      this.titlePosition = 'outside';
    }
  }

  private setUpMovieImg() {
    if (this.orientation === 'landscape') {
      this.movieImg = this.movie.tilePhotoUrl || this.defaultImage;
    } else {
      this.movieImg = this.movie.portraitPhotoUrl || this.defaultImage;
    }
  }

  private setUpReleaseMessage() {
    if (this.showReleaseMessage) {
      this.releaseMessage = this.movieService.getFormattedCinemasReleaseDate(this.movie);
    }
  }

  get defaultImage() {
    return this.channelService.getDefaultImg(this.orientation);
  }

  onMovieClick() {
    if (!this.navigation) {
      this.movieClick.emit(this.movie);
    }
  }

  playVideo(movie: Movie): void {
    this.videoService.play({ content: movie, category: 'MovieCardVideo' });
  }

  onWhereToWatch(movie: Movie): void {
    const showtimesAvailable = this.showtimeStreamingService.determineInCinemasAvailability(movie);
    const streamingAvailable = this.showtimeStreamingService.determineStreamingAvailability(movie);
    const movieDetailSection = showtimesAvailable ? 'in-cinemas' : streamingAvailable ? 'watch-at-home' : null;
    this.toMovieDetail({ params: { skipTrailer: true, ...(movieDetailSection && { section: movieDetailSection }) } });
  }

  toMovieDetail(values?: { params?: Params }): void {
    let route: string[];
    let { params } = { ...values };
    if (!params) params = {};
    const source = `${this.type.replace(/\b\w/g, char => char.toUpperCase())}Page`;
    this.trackingService.trackEvent({
      action: 'SelectMovie',
      properties: {
        category: 'MovieCTA',
        label: this.movie?.title ?? '',
        movie: this.movie?.title ?? '',
        idMovie: this.movie.id,
        imdb: this.movie?.imdb,
        tagsMovie: getMovieTags(this.movie, 'friendlyUrl'),
        genresMovie: getMovieGenres(this.movie),
        source: source
      }
    });
    if (this.env?.customFeatures?.nonPrefixedMovieDetailRoutes) {
      route = [this.movie.friendlyUrl];
    } else {
      route = ['movie', this.movie.friendlyUrl];
    }

    this.router.navigate(route, { queryParams: params, state: { movie: this.movie } });
  }
}
